// src/components/fundManagement/ShortcutNavigation.tsx

import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import React, { useEffect } from 'react';
import { FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'store';
import {
  checkUserCompetency,
  selectUserCompetency,
  selectCompetencyLoading,
  selectCompetencyError
} from 'slices/fundManagementInvestment/fundManagementConfigurationSlice';
import { useAppSelector } from 'app/hooks';

interface NavigationShortcut {
  title: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  path: string;
  isActive: boolean;
}

interface ShortcutNavigationProps {
  shortcuts: NavigationShortcut[];
  title: string;
  isAdmin: boolean;
}

const ShortcutNavigation: React.FC<ShortcutNavigationProps> = ({ shortcuts, title, isAdmin }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const competency = useSelector(selectUserCompetency); // "competent" | "incompetent" | null
  const competencyLoading = useSelector(selectCompetencyLoading);
  const competencyError = useSelector(selectCompetencyError);

  // Check competency once
  useEffect(() => {
    if (!selectedProjectId || competency !== null) return;
    dispatch(checkUserCompetency({ groupId: selectedProjectId }));
  }, [dispatch, selectedProjectId, competency]);

  // const isIncompetent = competency === 'incompetent';
  const isIncompetent = false;

  // If incompetent → show ONLY the message, hide all cards
  if (isIncompetent) {
    return (
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            mb: 3,
            color: '#333',
            fontSize: { xs: '0.85rem', md: '0.95rem' }
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            borderRadius: 3,
            bgcolor: 'rgba(238, 242, 255, 0.6)',
            border: '1px solid rgba(165, 180, 252, 0.4)',
            backdropFilter: 'blur(10px)',
            p: { xs: 3, md: 4 },
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(165, 180, 252, 0.15)'
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'rgba(165, 180, 252, 0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.5
            }}
          >
            <FaExclamationTriangle size={28} color="#6366f1" />
          </Box>

          <Typography variant="h6" fontWeight={700} color="#4f46e5" sx={{ mb: 1.5 }}>
            Competency Status Update Required
          </Typography>

          <Typography variant="body1" fontWeight={500} color="#3730a3" sx={{ mb: 2 }}>
            {competencyError?.message || 'Your investor competency has expired'}
          </Typography>

          <Typography
            variant="body2"
            color="#6366f1"
            lineHeight={1.7}
            sx={{ maxWidth: 480, mx: 'auto' }}
          >
            To continue accessing investment features such as buying or selling units, please
            contact your fund manager or relationship officer for further details and assistance.
          </Typography>
        </Box>
      </Box>
    );
  }

  // If competent or loading → show all cards
  return (
    <Box>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 500,
          mb: { xs: 1.5, md: 1.5 },
          color: '#333',
          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' }
        }}
      >
        {title}
      </Typography>

      {competencyLoading ? (
        <Typography color="text.secondary">Loading permissions...</Typography>
      ) : (
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
          {shortcuts.map((shortcut, index) => {
            const disabled = !shortcut.isActive;

            return (
              <Grid item xs={6} sm={4} md={3} lg={isAdmin ? 2.4 : 4} key={index}>
                <Card
                  sx={{
                    height: { xs: 100, sm: 120, md: 130 },
                    borderRadius: 2,
                    background: disabled ? 'rgba(245,245,245,0.9)' : 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: disabled ? 'none' : 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    boxShadow: '0 3px 12px rgba(0, 0, 0, 0.08)',
                    opacity: disabled ? 0.6 : 1,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: disabled ? 'none' : 'translateY(-2px)',
                      boxShadow: disabled ? 'none' : '0 6px 20px rgba(0, 0, 0, 0.12)'
                    }
                  }}
                >
                  {!shortcut.isActive && (
                    <Chip
                      label="Soon"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        fontSize: '0.55rem',
                        backgroundColor: 'rgba(255,99,71,0.15)',
                        color: '#d32f2f',
                        height: 16
                      }}
                    />
                  )}

                  <CardActionArea
                    onClick={() => !disabled && navigate(shortcut.path, { state: { isAdmin } })}
                    disabled={disabled}
                    sx={{ height: '100%' }}
                  >
                    <CardContent
                      sx={{
                        padding: { xs: 1.2, sm: 1.5 },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textAlign: 'center'
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 28, sm: 32, md: 36 },
                            height: { xs: 28, sm: 32, md: 36 },
                            borderRadius: 1.5,
                            background: disabled ? '#ccc' : shortcut.gradient,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: { xs: 0.8, sm: 1 },
                            boxShadow: disabled ? 'none' : '0 3px 10px rgba(0,0,0,0.15)'
                          }}
                        >
                          {React.cloneElement(shortcut.icon as React.ReactElement<any>, {
                            color: disabled ? '#888' : 'white',
                            size: 16
                          })}
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                            color: disabled ? '#999' : '#333'
                          }}
                        >
                          {shortcut.title}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            color: disabled ? '#aaa' : '#666',
                            fontSize: { xs: '0.55rem', sm: '0.6rem' },
                            display: { xs: 'none', sm: 'block' }
                          }}
                        >
                          {shortcut.description}
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        endIcon={<FaArrowRight size={7} />}
                        disabled={disabled}
                        sx={{ textTransform: 'none', fontWeight: 500, fontSize: '0.6rem' }}
                      >
                        Access
                      </Button>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default ShortcutNavigation;
